import { useEffect } from 'react';
import { useLocation } from 'react-router';

interface MetaTagsProps {
  title?: string;
  description?: string;
  noIndex?: boolean;
}

/**
 * Component to manage meta tags for SEO
 * Ensures only the homepage is indexed by search engines
 */
export default function MetaTags({
  title,
  description,
  noIndex = false,
}: MetaTagsProps) {
  const location = useLocation();

  useEffect(() => {
    // Determine if current page should be indexed
    const isHomePage = location.pathname === '/';
    const shouldIndex = isHomePage && !noIndex;

    // Update title
    if (title) {
      document.title = title;
    } else if (isHomePage) {
      document.title =
        'Oil & Gas Investment Estimator – Start Your Tax Strategy | Fieldvest';
    } else {
      // For non-home pages, use a simpler title
      document.title = 'Fieldvest';
    }

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      if (description) {
        metaDescription.setAttribute('content', description);
      } else if (isHomePage) {
        metaDescription.setAttribute(
          'content',
          "Start optimizing your taxes today. Fieldvest's onboarding tool helps accredited investors match with U.S. energy projects and estimate first-year tax deductions."
        );
      }
    }

    // Update robots meta tag
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }

    // Set robots directive based on page
    if (shouldIndex) {
      robotsMeta.setAttribute('content', 'index, follow');
    } else {
      robotsMeta.setAttribute('content', 'noindex, nofollow');
    }

    // Update canonical URL (always point to homepage for SEO)
    let canonicalLink = document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }

    // Only set canonical to actual page URL if it's the homepage
    if (isHomePage) {
      canonicalLink.setAttribute('href', 'https://app.fieldvest.com/');
    } else {
      // For other pages, still point canonical to homepage to consolidate SEO
      canonicalLink.setAttribute('href', 'https://app.fieldvest.com/');
    }

    // Update Open Graph URL
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://app.fieldvest.com/');
    }
  }, [location.pathname, title, description, noIndex]);

  return null;
}
